import { Fragment } from 'react';

import { Dialog, Transition } from '@headlessui/react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import cx from 'classnames';

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  message: string;
  children?: React.ReactElement<any> | number | string;
  panelSizeClass?: string;
}

const Modal = ({ isOpen, onClose, title, message, children, panelSizeClass = 'max-w-md' }: ModalProps) => {
  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-50"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-50"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 z-20 bg-black/60" />
        </Transition.Child>

        <div className="fixed inset-0 z-20 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-50"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-50"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel
                className={cx(
                  'relative -mt-32 w-full transform overflow-auto rounded-md bg-white p-6 text-left align-middle shadow-xl transition-all',
                  panelSizeClass,
                )}
              >
                <button aria-label="Close" className="absolute right-4 top-4" onClick={onClose}>
                  <XMarkIcon className="h-6 w-6 stroke-1 text-gray-400 transition-all hover:text-gray-700" />
                </button>
                <Dialog.Title as="h3" className="tracking-medium pb-1 text-xl font-bold leading-tight text-gray-900">
                  {title}
                </Dialog.Title>
                <Dialog.Description
                  as="h5"
                  className="mb-4 pb-1 text-sm font-light leading-tight tracking-wider text-gray-500"
                >
                  {message}
                </Dialog.Description>
                <div className="w-full">{children}</div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default Modal;
